// @react
import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
// @types
import { GetUserQuery, GetUserQueryResult } from 'lib/graphql/graphqlTypes'
import { ContactFormValues } from 'modules/common/forms/types/contact/'
// @graphQL
import { ApolloQueryResult } from '@apollo/client'
// @libraries
import { DevTool } from '@hookform/devtools'
import { makeStyles } from '@material-ui/core/styles'
// @common
import strings from 'lib/constants/strings'
// @components
import Spacer from '@bit/atd.web.spacer'
import Container from '@bit/atd.web.container'
import Grid from '@bit/atd.web.grid'
import Banner from '@bit/atd.web.banner'
import Divider from '@bit/atd.web.divider'
import Box from '@bit/atd.web.box'
import SkeletonLoader from '@bit/atd.web.loaders.skeleton'
// @module component
import EmailField from 'modules/common/forms/fields/contact/Email'
import PhoneFieldsArray from 'modules/common/forms/fields/contact/Phones'
import AddressFieldsArray from 'modules/common/forms/fields/contact/Address'
import Navigation from 'modules/onBoarding/components/_common/Navigation'
import colors from '@bit/atd.web.colors/dist/colors'
import useFormWrapper from 'modules/common/forms/hooks/useContactFormWrapper'
import useNavigation from '../../../hooks/useNavigation'
import { Slide } from '@material-ui/core'
import { Direction, NavigationContext } from 'modules/common/context/navigation'

const useStyles = makeStyles(() => ({
  container: {
    maxWidth: '768px',
    margin: '0 auto',
    '& .MuiPaper-root': {
      width: '100%',
    },
  },
  formContainer: {
    position: 'relative',
  },
}))

type PropsType = {
  data?: GetUserQueryResult['data']
  isLoading: boolean
  refetch: () => Promise<ApolloQueryResult<GetUserQuery>>
}

const Contact = ({
  data: userData,
  isLoading,
  refetch,
}: PropsType): ReactElement => {
  /****************************************
   * STATE
   *****************************************/

  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [slidingIn, setSlidingIn] = useState<boolean>(false)
  const [destination, setDestination] = useState<string>('')
  const [direction, setDirection] = useState<Direction>(Direction.LEFT)

  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()
  const navigation = useContext(NavigationContext)
  const history = useHistory()

  useEffect(() => {
    setSlidingIn(true)
  }, [])

  const {
    formMethods,
    control,
    userContactInfoUpdateHandler,
    userAddressDeleteHandler,
    userPhoneDeleteHandler,
  } = useFormWrapper({
    data: userData,
    isLoading,
    refetch,
  })

  const { formState } = formMethods
  const { errors } = formState
  const {
    formState: { dirtyFields },
  } = formMethods
  const isFormDirty = Object.keys(dirtyFields).length > 0

  const { handleNextClick, handleSaveExit, handlePrevClick } = useNavigation({
    setDestination,
    setDirection,
    setSlidingIn,
    setIsSubmitting,
    isFormDirty,
    updateHandler: userContactInfoUpdateHandler,
  })

  /****************************************
   * FORM HANDLERS
   *****************************************/

  /**
   *
   * @param id
   * @param type
   */
  const onRemoveHandler = async (
    id: number,
    type: 'phone' | 'address',
  ): Promise<boolean> => {
    setIsSubmitting(true)
    switch (type) {
      case 'phone': {
        const res = await userPhoneDeleteHandler(id)
        setIsSubmitting(false)
        // @todo this is probably a bit crude to cancel the whole form on remove item
        // we delete mutation item right away on click remove, instead of waiting for save
        // it's going to get too complex for now if we gather all deletes, edits + additions before onSave
        // however we probably want an 'are you sure' popup for delete valid item
        // i.e. --> click remove on valid item
        // check if item has valid ID (not index, but entity ID like addressID)
        //
        // --> (item with saved data) modal 'are you sure'
        // --> submit mutation
        // --> remove() formArray item,
        // onCancelHandler()
        // if NOT valid item (i.e. empty or unsaved item w/ no entity id ), remove button should remove from fieldArray only
        // no modal
        // stay in form edit mode
        return res
      }
      case 'address': {
        const res = await userAddressDeleteHandler(id)
        setIsSubmitting(false)
        // @todo this is probably a bit crude to cancel the whole form on remove item
        // i've used this here because now we get validation error on required fields when removing empty item
        return res
      }
    }

    return false
  }

  /******************************************
   * RENDER
   ******************************************/

  const box = (content: ReactElement, isLoading: boolean) => {
    return <Box>{!isLoading ? content : <SkeletonLoader />}</Box>
  }

  return (
    <>
      {process.env.NODE_ENV !== 'production' && <DevTool control={control} />}
      <Spacer size={32} fullWidth />

      {/* Form */}
      <form className={classes.formContainer}>
        <Slide
          in={slidingIn}
          timeout={500}
          direction={navigation.direction}
          unmountOnExit
          onExit={() => {
            formMethods.clearErrors()
          }}
          onExited={() => {
            navigation.setDirection(direction)
            history.push(destination)
          }}
        >
          <div>
            <Container>
              <Grid className={classes.container} container>
                {box(
                  <Grid container spacing={4}>
                    {/* Banner */}
                    <Grid xs={12} item>
                      <Banner fullWidth icon="lock-on">
                        None of the following will be visible on your public
                        profile:
                      </Banner>
                    </Grid>

                    <PhoneFieldsArray
                      formMethods={formMethods}
                      control={control}
                      errors={errors}
                      onRemove={(id) => onRemoveHandler(id, 'phone')}
                    />

                    <Grid xs={12} item>
                      <Divider
                        width="100%"
                        color={colors.gray300}
                        height="1px"
                      />
                    </Grid>

                    <EmailField control={control} errors={errors} />

                    <Grid xs={12} item>
                      <Divider
                        width="100%"
                        color={colors.gray300}
                        height="1px"
                      />
                    </Grid>

                    <AddressFieldsArray
                      formMethods={formMethods}
                      control={control}
                      errors={errors}
                      onRemove={(id) => onRemoveHandler(id, 'address')}
                    />
                  </Grid>,
                  isLoading,
                )}
              </Grid>
            </Container>
          </div>
        </Slide>

        <Navigation
          handleExitOnClick={formMethods.handleSubmit(
            (data: ContactFormValues) => handleSaveExit(data),
            (errors) => handleSaveExit(undefined, errors),
          )}
          handlePrevOnClick={formMethods.handleSubmit(
            (data: ContactFormValues) => handlePrevClick(data),
            (errors) => handlePrevClick(undefined, errors),
          )}
          handleNextOnClick={formMethods.handleSubmit(
            (data: ContactFormValues) => handleNextClick(data),
            (errors) => handleNextClick(undefined, errors),
          )}
          prevPage={strings.ONBOARDING.EDUCATION.HEADER.TITLE}
          nextPage={strings.ONBOARDING.COMMUNICATION.HEADER.TITLE}
          isSubmitting={isMutationSubmitting}
          prevDisable={isLoading || isMutationSubmitting}
          exitDisable={isLoading || isMutationSubmitting}
          nextDisable={isLoading || isMutationSubmitting}
        />
      </form>
    </>
  )
}

export default Contact
